import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Button, Group, Modal, MultiSelect, Skeleton, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck, IconX } from "@tabler/icons-react";
import { useContext, useEffect, useMemo, useState } from "react";

interface ClassroomSongAssignorProps {
	classroom: Classroom | null;
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	onDuplication?: () => void;
}

type SelectOption = { label: string; value: string };

const ClassroomSongAssignor: React.FC<ClassroomSongAssignorProps> = ({
	classroom,
	showModal,
	setShowModal,
	onDuplication,
}) => {
	const [isFetching, setIsFetching] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [songs, setSongs] = useState<Song[] | null | undefined>(undefined);

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("ClassroomSongAssignor must be a child of SessionContext.");
	}
	const token = session.token;
	if (!token) return null;

	// MultiSelect wants string[] of selected ids
	const form = useForm<{ songs: string[] }>({
		initialValues: {
			songs: [],
		},
	});

	const fetchSongs = () => {
		setIsFetching(true);

		ConductionAPI.getAllSongs(token)
			.then((loadedSongs: Song[] | null) => {
				if (!loadedSongs || loadedSongs.length === 0) {
					setSongs(null);
				} else {
					setSongs(
						loadedSongs.sort((a, b) => b.uploadDate.getTime() - a.uploadDate.getTime()) // Newest first
					);
				}
			})
			.catch(() => {
				setSongs(null);
			})
			.finally(() => setIsFetching(false));
	};

	useEffect(() => {
		fetchSongs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	// Build select options from songs
	const songOptions: SelectOption[] = useMemo(() => {
		if (!songs) return [];
		return songs.map((s) => {
			const composers = s.artists.filter((a) => a.artistType === "Composer");
			const label = `"${s.songName}" by ${composers
				.map((a, i) => `${composers.length > 1 && i === composers.length - 1 ? "and " : ""}${a.artistName}`)
				.join(composers.length > 2 ? ", " : " ")}`;
			return { label, value: s.songId };
		});
	}, [songs]);

	// Initialize selection from classroom.defaultSongIds, but only ids that exist in current options
	useEffect(() => {
		if (!classroom || !songOptions.length) return;
		const available = new Set(songOptions.map((o) => o.value));
		const initial = (classroom.defaultSongIds ?? []).filter((id) => available.has(id));
		form.setFieldValue("songs", initial);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [classroom?.id, songOptions]);

	const addAllSongs = () => form.setFieldValue("songs", songOptions.map((o) => o.value));
	const clearAllSongs = () => form.setFieldValue("songs", []);

	const handleSubmit = async () => {
		if (classroom === null) return;

		const { hasErrors } = form.validate();
		if (hasErrors) return;

		setIsSubmitting(true);

		const notificationId = Utils.displayNotification("Assigning Songs to Classroom", undefined, "blue", "loading");

		const songIds: string[] = form.values.songs; // already string[]

		try {
			await ConductionAPI.updateClassroomSongAssignments(classroom.id, songIds, token);

			Utils.updateNotification(notificationId, "Successfully Assigned Songs", undefined, "green", <IconCheck size={16} />);

			onDuplication?.();
			setShowModal(false);
		} catch (error) {
			console.error("Error assigning to classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to assign song to classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal opened={showModal} onClose={() => setShowModal(false)} title="Assign Songs to Classroom" centered size="xl">
			{isFetching || songs === undefined ? (
				<Skeleton height={"100%"} radius="md" />
			) : classroom === null || songs === null ? (
				<Text>The needed data did not load correctly.</Text>
			) : (
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack gap={0}>
						<Text size="sm">Which songs should be available to the {classroom.name} classroom?</Text>
						<Group wrap="nowrap" align="flex-start">
							<MultiSelect
								data={songOptions}
								{...form.getInputProps("songs")}
								searchable
								clearable
								style={{ flex: 1 }}
							/>
							{/* Add all */}
							<Button
								type="button"
								size="compact-xs"
								variant="subtle"
								onClick={addAllSongs}
								disabled={songOptions.length === 0 || form.values.songs.length === songOptions.length}
								leftSection={<IconCheck size={16} />}
							>
								Add All
							</Button>
							{/* Clear all */}
							<Button
								type="button"
								size="compact-xs"
								variant="subtle"
								onClick={clearAllSongs}
								disabled={form.values.songs.length === 0}
								leftSection={<IconX size={16} />}
							>
								Clear
							</Button>
						</Group>
						<Button type="submit" radius="xl" size="sm" loading={isSubmitting}>
							Assign Songs to Classroom
						</Button>
					</Stack>
				</form>
			)}
		</Modal>
	);
};

export default ClassroomSongAssignor;
